/**
 * LiveCounter — animated trio of social-proof numbers on the home page.
 *
 *   🐪 47 عرض نشط  ·  🏆 3 بطولات قادمة  ·  🤖 1,200+ تحليل AI
 *
 * Numbers ease from 0 → real value over ~1.4s using requestAnimationFrame.
 * Polls /api/public/live-stats once on mount; cheap & safe to refresh on
 * route changes by mounting the component again.
 */
import React, { useEffect, useState } from "react";
import api from "../../lib/api";

function useEasedCount(target, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

export default function LiveCounter({ ar = true }) {
  const [stats, setStats] = useState({ listings: 0, championships: 0, ai_analyses: 0 });

  useEffect(() => {
    api.get("/public/live-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const listings = useEasedCount(stats.listings);
  const champs   = useEasedCount(stats.championships);
  const ai       = useEasedCount(stats.ai_analyses);

  const items = [
    { emoji: "🐪", value: listings, label: ar ? "عرض نشط" : "active listings" },
    { emoji: "🏆", value: champs,   label: ar ? "بطولة قادمة" : "championships" },
    { emoji: "🤖", value: ai,       label: ar ? "تحليل AI" : "AI analyses", suffix: "+" },
  ];

  return (
    <div
      className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 via-black/40 to-black/60 p-3"
      data-testid="live-counter"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] font-black uppercase tracking-wider text-emerald-300">
          {ar ? "نشاط ثقة الآن" : "Thqah live now"}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {items.map((it, i) => (
          <div key={i} className="text-center" data-testid={`live-counter-${i}`}>
            <div className="text-lg leading-none mb-1">{it.emoji}</div>
            <div className="text-lg font-black gold-text leading-none font-mono">
              {it.value.toLocaleString()}{it.suffix || ""}
            </div>
            <div className="text-[10px] text-white/55 mt-1 font-bold">{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
